import { Fragment, useRef, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { useConfig } from '../store';
import { FENDER_STYLES, PAINTS, WHEEL_FINISHES, fenderColor, paintByHex } from './truck/paints';

interface RadioOption {
  key: string;
  checked: boolean;
  onSelect: () => void;
  selectOnArrow?: boolean;
  label?: string;
  className: string;
  style?: CSSProperties;
  children?: ReactNode;
  overlay?: ReactNode;
}

const ARROW_STEP: Record<string, number> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

const cssVars = (vars: Record<`--${string}`, string>) => vars as CSSProperties;

function RadioRow({ labelledBy, className, options }: { labelledBy: string; className: string; options: RadioOption[] }) {
  const buttons = useRef<(HTMLButtonElement | null)[]>([]);
  const tabStop = Math.max(0, options.findIndex((o) => o.checked));

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const step = ARROW_STEP[e.key];
    if (step === undefined) return;
    e.preventDefault();
    const from = buttons.current.findIndex(
      (b) => b !== null && (b === document.activeElement || (b.parentElement !== e.currentTarget && b.parentElement?.contains(document.activeElement))),
    );
    const to = (from + step + options.length) % options.length;
    const target = buttons.current[to];
    if (!target) throw new Error(`Radio option ${options[to].key} is not mounted`);
    target.focus();
    if (options[to].selectOnArrow !== false) options[to].onSelect();
  };

  return (
    <div role="radiogroup" aria-labelledby={labelledBy} className={className} onKeyDown={onKeyDown}>
      {options.map((o, i) => {
        const button = (
          <button
            ref={(el) => {
              buttons.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={o.checked}
            aria-label={o.label}
            tabIndex={i === tabStop ? 0 : -1}
            onClick={o.onSelect}
            className={o.className}
            style={o.style}
          >
            {o.children}
          </button>
        );
        return o.overlay ? (
          <span key={o.key} className="paint-slot relative flex flex-none">
            {button}
            {o.overlay}
          </span>
        ) : (
          <Fragment key={o.key}>{button}</Fragment>
        );
      })}
    </div>
  );
}

function OptionHead({ id, title, name, aside }: { id: string; title: string; name: string; aside?: ReactNode }) {
  return (
    <>
      <div className="flex items-baseline justify-between gap-3">
        <h2 id={id} className="text-sm font-medium text-white/90">
          {title}
        </h2>
        {aside}
      </div>
      <p className="mt-px min-h-4 font-mono text-[11px] uppercase leading-4 tracking-[0.16em] text-white/60">{name}</p>
    </>
  );
}

export function PaintPanel() {
  const bodyColor = useConfig((s) => s.bodyColor);
  const fenders = useConfig((s) => s.fenders);
  const wheels = useConfig((s) => s.wheels);
  const setTruck = useConfig((s) => s.setTruck);
  const picker = useRef<HTMLInputElement>(null);

  const paint = paintByHex(bodyColor);
  const custom = paint === undefined;
  const fenderName = FENDER_STYLES.find((f) => f.value === fenders)?.name;
  const wheelName = WHEEL_FINISHES.find((w) => w.value === wheels)?.name;
  if (!fenderName || !wheelName) throw new Error(`Unknown fender "${fenders}" or wheel "${wheels}" option`);

  const openPicker = () => {
    const input = picker.current;
    if (!input) throw new Error('Custom colour input is not mounted');
    input.click();
  };

  return (
    <div className="flex flex-col gap-[22px]">
      <section aria-labelledby="paint-body">
        <OptionHead
          id="paint-body"
          title="Body"
          name={paint?.name ?? 'Su misura'}
          aside={<span className="font-mono text-xs tracking-[0.04em] text-white/50 tabular-nums">{bodyColor.toUpperCase()}</span>}
        />
        <RadioRow
          labelledBy="paint-body"
          className="mt-3 flex items-center justify-between gap-2 wide:gap-1.5"
          options={[
            ...PAINTS.map((p) => ({
              key: p.slug,
              checked: paint?.slug === p.slug,
              onSelect: () => setTruck({ bodyColor: p.hex }),
              label: `${p.name}, ${p.hex.toUpperCase()}`,
              className: 'paint-chip size-[30px] wide:size-6',
              style: cssVars({ '--c': p.hex }),
            })),
            {
              key: 'custom',
              checked: custom,
              onSelect: openPicker,
              selectOnArrow: false,
              label: custom ? `Su misura, ${bodyColor.toUpperCase()}` : 'Su misura: pick a custom colour',
              className: 'paint-custom h-[30px] wide:h-6',
              style: cssVars({ '--custom': bodyColor }),
              children: (
                <>
                  {custom ? <span aria-hidden="true" className="paint-custom-dot" /> : <span aria-hidden="true" className="w-2.5 text-center text-sm leading-none text-white/60">+</span>}
                  Su misura
                </>
              ),
              overlay: (
                <input
                  ref={picker}
                  type="color"
                  tabIndex={-1}
                  aria-hidden="true"
                  value={bodyColor}
                  onChange={(e) => setTruck({ bodyColor: e.target.value })}
                  className="absolute inset-0 size-full cursor-pointer rounded-full border-0 p-0 opacity-0"
                />
              ),
            },
          ]}
        />
      </section>

      <section aria-labelledby="paint-fenders">
        <OptionHead id="paint-fenders" title="Fenders" name={fenderName} />
        <RadioRow
          labelledBy="paint-fenders"
          className="mt-3 flex flex-wrap items-center gap-2.5 wide:gap-2"
          options={FENDER_STYLES.map((f) => ({
            key: f.value,
            checked: fenders === f.value,
            onSelect: () => setTruck({ fenders: f.value }),
            className: 'paint-pill h-[34px] wide:h-[30px]',
            children: (
              <>
                <span aria-hidden="true" className="paint-split" style={cssVars({ '--a': bodyColor, '--b': fenderColor(bodyColor, f.value) })} />
                {f.name}
              </>
            ),
          }))}
        />
      </section>

      <section aria-labelledby="paint-wheels">
        <OptionHead id="paint-wheels" title="Wheels" name={wheelName} />
        <RadioRow
          labelledBy="paint-wheels"
          className="mt-3 flex flex-wrap items-center gap-2.5 wide:gap-2"
          options={WHEEL_FINISHES.map((w) => ({
            key: w.value,
            checked: wheels === w.value,
            onSelect: () => setTruck({ wheels: w.value }),
            label: w.name,
            className: `paint-chip size-[30px] wide:size-6${w.value === 'cromo' ? ' paint-chip-metal' : ''}`,
            style: cssVars({ '--c': w.color }),
          }))}
        />
      </section>
    </div>
  );
}
